import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import axios from "axios";
import { ROOT_URL } from "../../api/ApiConfig";

function authHeaders(getState) {
  const token = getState().loginToken?.response?.token;
  return {
    headers: {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
    },
  };
}

function getBusinessId(getState) {
  return getState().loginToken?.response?.user?.business_id;
}

function businessEmailFromPayload(payload) {
  if (payload == null || typeof payload !== "object") return "";
  const raw =
    payload?.data?.business_email ??
    payload?.business_email ??
    payload?.data?.email ??
    payload?.email;
  if (raw == null) return "";
  return String(raw).trim();
}

function hierarchyFromPayload(payload) {
  if (payload == null || typeof payload !== "object") return [];
  const raw =
    payload?.data?.hierarchy ??
    payload?.hierarchy ??
    payload?.data?.email_hierarchy;
  return Array.isArray(raw) ? raw : [];
}

function personalTicketFromEmailFromPayload(payload) {
  if (payload == null || typeof payload !== "object") return "";
  const data = payload?.data ?? payload;
  const direct =
    data?.ticket_from_email ??
    payload?.ticket_from_email;
  if (direct != null && String(direct).trim()) {
    return String(direct).trim();
  }
  const hierarchy = hierarchyFromPayload(payload);
  const agentRow = hierarchy.find((row) => row?.key === "agent_linked_email");
  if (agentRow?.value != null && String(agentRow.value).trim()) {
    return String(agentRow.value).trim();
  }
  return "";
}

export const fetchBusinessEmail = createAsyncThunk(
  "businessEmail/fetch",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/business/${businessId}/groups/${groupId}/business-email`,
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      if (error.response?.status === 404) {
        return rejectWithValue({ groupId, notFound: true, status: 404 });
      }
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const patchBusinessEmail = createAsyncThunk(
  "businessEmail/patch",
  async ({ groupId, business_email }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.patch(
        `${ROOT_URL}/business/${businessId}/groups/${groupId}/business-email`,
        { business_email: business_email.trim() },
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const patchPersonalTicketFromEmail = createAsyncThunk(
  "businessEmail/patchPersonal",
  async ({ groupId, ticket_from_email }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.patch(
        `${ROOT_URL}/business/${businessId}/groups/${groupId}/business-email`,
        { ticket_from_email: ticket_from_email.trim() },
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const deleteBusinessEmail = createAsyncThunk(
  "businessEmail/delete",
  async ({ groupId }, { getState, rejectWithValue }) => {
    if (!groupId) {
      return rejectWithValue({ message: "Missing group id" });
    }
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.delete(
        `${ROOT_URL}/business/${businessId}/groups/${groupId}/business-email`,
        authHeaders(getState),
      );
      return { groupId, payload: response.data };
    } catch (error) {
      return rejectWithValue(
        error.response?.data || { message: error.message, groupId },
      );
    }
  },
);

export const fetchAllBusinessEmails = createAsyncThunk(
  "businessEmail/fetchAll",
  async (_, { getState, rejectWithValue }) => {
    const businessId = getBusinessId(getState);
    if (!businessId) {
      return rejectWithValue({ message: "Missing business id" });
    }
    try {
      const response = await axios.get(
        `${ROOT_URL}/business/${businessId}/groups/business-emails`,
        authHeaders(getState),
      );
      return response.data;
    } catch (error) {
      return rejectWithValue(error.response?.data || { message: error.message });
    }
  },
);

const initialState = {
  groupId: null,
  business_email: "",
  ticket_from_email: "",
  hierarchy: [],
  loading: false,
  saving: false,
  deleting: false,
  error: null,
  loaded: false,
  allGroups: [],
  allLoading: false,
  allError: null,
};

const businessEmailApi = createSlice({
  name: "businessEmail",
  initialState,
  reducers: {
    clearBusinessEmail: () => initialState,
    clearBusinessEmailError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchBusinessEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
        state.loaded = false;
      })
      .addCase(fetchBusinessEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.groupId = action.payload.groupId;
        state.business_email = businessEmailFromPayload(action.payload.payload);
        state.ticket_from_email = personalTicketFromEmailFromPayload(action.payload.payload);
        state.hierarchy = hierarchyFromPayload(action.payload.payload);
      })
      .addCase(fetchBusinessEmail.rejected, (state, action) => {
        state.loading = false;
        state.loaded = true;
        state.groupId = action.payload?.groupId ?? action.meta?.arg?.groupId ?? null;
        if (action.payload?.notFound) {
          state.business_email = "";
          state.ticket_from_email = "";
          state.hierarchy = [];
          state.error = null;
        } else {
          state.error = action.payload;
        }
      })
      .addCase(patchBusinessEmail.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(patchBusinessEmail.fulfilled, (state, action) => {
        state.saving = false;
        state.groupId = action.payload.groupId;
        const fromApi = businessEmailFromPayload(action.payload.payload);
        const fromArg = action.meta?.arg?.business_email;
        state.business_email = String(fromApi || fromArg || "").trim();
        const hierarchy = hierarchyFromPayload(action.payload.payload);
        if (hierarchy.length) state.hierarchy = hierarchy;
      })
      .addCase(patchBusinessEmail.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(patchPersonalTicketFromEmail.pending, (state) => {
        state.saving = true;
        state.error = null;
      })
      .addCase(patchPersonalTicketFromEmail.fulfilled, (state, action) => {
        state.saving = false;
        state.groupId = action.payload.groupId;
        const fromApi = personalTicketFromEmailFromPayload(action.payload.payload);
        const fromArg = action.meta?.arg?.ticket_from_email;
        state.ticket_from_email = String(fromApi || fromArg || "").trim();
        const hierarchy = hierarchyFromPayload(action.payload.payload);
        if (hierarchy.length) state.hierarchy = hierarchy;
      })
      .addCase(patchPersonalTicketFromEmail.rejected, (state, action) => {
        state.saving = false;
        state.error = action.payload;
      })
      .addCase(deleteBusinessEmail.pending, (state) => {
        state.deleting = true;
        state.error = null;
      })
      .addCase(deleteBusinessEmail.fulfilled, (state, action) => {
        state.deleting = false;
        state.groupId = action.payload.groupId;
        state.business_email = "";
        const hierarchy = hierarchyFromPayload(action.payload.payload);
        if (hierarchy.length) state.hierarchy = hierarchy;
      })
      .addCase(deleteBusinessEmail.rejected, (state, action) => {
        state.deleting = false;
        state.error = action.payload;
      })
      .addCase(fetchAllBusinessEmails.pending, (state) => {
        state.allLoading = true;
        state.allError = null;
      })
      .addCase(fetchAllBusinessEmails.fulfilled, (state, action) => {
        state.allLoading = false;
        const raw = action.payload?.data ?? action.payload;
        state.allGroups = Array.isArray(raw) ? raw : [];
      })
      .addCase(fetchAllBusinessEmails.rejected, (state, action) => {
        state.allLoading = false;
        state.allError = action.payload;
        state.allGroups = [];
      });
  },
});

export const { clearBusinessEmail, clearBusinessEmailError } =
  businessEmailApi.actions;

export default businessEmailApi.reducer;
